import { events, EVT } from '../utils/events.js';
import { hasProfanity } from '../utils/profanity.js';

/**
 * Manages all HTML UI overlays — HUD, galaxy map, inventory, claim dialog, notifications.
 */
export class UIManager {
  constructor(gameState) {
    this.gameState = gameState;

    // Cache DOM elements
    this.hud = document.getElementById('hud');
    this.questProgress = document.getElementById('quest-progress');
    this.objectiveLine = document.getElementById('objective-line');
    this.systemName = document.getElementById('system-name');
    this.planetNameEl = document.getElementById('planet-name');
    this.controlsHint = document.getElementById('controls-hint');
    this.musicLabel = document.getElementById('music-label');
    this.actionButton = document.getElementById('mobile-action-btn');

    this.shipConsole = document.getElementById('ship-console');
    this.inventoryPanel = document.getElementById('inventory-panel');
    this.creatureList = document.getElementById('creature-list');
    this.claimDialog = document.getElementById('claim-dialog');
    this.claimNameInput = document.getElementById('claim-name');
    this.notificationArea = document.getElementById('notification-area');
    this.mapInfo = document.getElementById('map-info');

    // Share Dialog
    this.shareDialog = document.getElementById('share-dialog');
    this.shareUrlInput = document.getElementById('share-url-input');

    // Audio Log
    this.audioLogDialog = document.getElementById('audio-log-dialog');
    this.logTitle = document.getElementById('log-title');
    this.logContent = document.getElementById('log-content');
    this.logToolFound = document.getElementById('log-tool-found');

    this.interactPrompt = null;
    this._createInteractPrompt();

    this._bindButtons();
    this._bindEvents();
  }

  _createInteractPrompt() {
    this.interactPrompt = document.createElement('div');
    this.interactPrompt.id = 'interact-prompt';
    document.body.appendChild(this.interactPrompt);
  }

  _bindButtons() {
    document.getElementById('btn-close-journal')?.addEventListener('click', () => {
      events.emit(EVT.JOURNAL_CLOSE);
    });
    document.getElementById('btn-close-tools')?.addEventListener('click', () => {
      events.emit('tools:close');
    });
    document.getElementById('btn-close-console').addEventListener('click', () => {
      events.emit(EVT.MAP_CLOSE);
    });
    document.getElementById('btn-close-inventory').addEventListener('click', () => this.hideInventory());
    document.getElementById('btn-close-log').addEventListener('click', () => this.hideAudioLog());
    document.getElementById('btn-share-close').addEventListener('click', () => this.hideShareDialog());
    document.getElementById('btn-share-copy').addEventListener('click', async () => {
      this.shareUrlInput.select();
      try {
        await navigator.clipboard.writeText(this.shareUrlInput.value);
        this.showNotification('Copied to clipboard!');
      } catch (e) {
        // Clipboard API is unavailable on non-secure origins (e.g. LAN dev URLs)
        const ok = document.execCommand && document.execCommand('copy');
        this.showNotification(ok ? 'Copied to clipboard!' : 'Copy failed — select the link and copy manually');
      }
    });
    // Clicking the dark backdrop dismisses the dialog
    this.shareDialog.addEventListener('click', (e) => {
      if (e.target === this.shareDialog) this.hideShareDialog();
    });
    document.getElementById('btn-claim-cancel').addEventListener('click', () => this.hideClaimDialog());
    document.getElementById('btn-claim-confirm').addEventListener('click', () => this._confirmClaim());
    document.getElementById('btn-warp')?.addEventListener('click', () => {
      if (this._selectedWarpTarget) {
        events.emit(EVT.WARP_START, this._selectedWarpTarget);
      }
    });
  }

  _bindEvents() {
    events.on(EVT.NOTIFICATION, (msg) => this.showNotification(msg));
    events.on(EVT.MUSIC_UPDATE, ({ scale, root, genre, timeSig }) => {
      const genreLabel = genre ? ` · ${genre}` : '';
      const tsLabel = timeSig ? ` · ${timeSig[0]}/${timeSig[1]}` : '';
      this.musicLabel.textContent = `${root} ${scale}${genreLabel}${tsLabel}`;
    });
    events.on(EVT.MAP_OPEN, () => this.showConsole());
    events.on(EVT.MAP_CLOSE, () => this.hideConsole());
  }

  showHUD() {
    this.hud.classList.remove('hidden');
    // Quest progress + objective live in the top-center stack (outside #hud)
    // so notifications can layer above modals — mirror HUD visibility here.
    this.questProgress?.classList.remove('hidden');
    this.objectiveLine?.classList.remove('hidden');
  }

  hideHUD() {
    this.hud.classList.add('hidden');
    this.questProgress?.classList.add('hidden');
    this.objectiveLine?.classList.add('hidden');
  }

  updateActionButton(text) {
    if (!this.actionButton) return;
    this.actionButton.textContent = text;
    this.actionButton.style.display = 'inline-flex';
  }

  resetActionButton() {
    if (!this.actionButton) return;
    this.actionButton.textContent = 'A';
    this.actionButton.style.display = 'inline-flex';
  }

  hideActionButton() {
    if (!this.actionButton) return;
    this.actionButton.style.display = 'none';
  }

  showActionButton(text = 'A') {
    if (!this.actionButton) return;
    this.actionButton.textContent = text;
    this.actionButton.style.display = 'inline-flex';
  }

  updateLocation(systemName, planetName) {
    this.systemName.textContent = systemName;
    this.planetNameEl.textContent = planetName;
  }

  updateMusicLabel(text) {
    if (this.musicLabel) this.musicLabel.textContent = text;
  }

  updateControlsHint(text) {
    this.controlsHint.textContent = text;
  }

  showInteractPrompt(text) {
    this.interactPrompt.textContent = text;
    this.interactPrompt.classList.add('visible');
  }

  hideInteractPrompt() {
    this.interactPrompt.classList.remove('visible');
  }

  // ── Console / Galaxy Map ──
  showConsole() {
    this.shipConsole.classList.remove('hidden');
    const mobileControls = document.getElementById('mobile-controls');
    if (mobileControls) mobileControls.classList.add('console-open');
  }

  hideConsole() {
    this.shipConsole.classList.add('hidden');
    const mobileControls = document.getElementById('mobile-controls');
    if (mobileControls) mobileControls.classList.remove('console-open');
  }

  setWarpTarget(system) {
    this._selectedWarpTarget = system;
    if (system) {
      let info = `${system.name} — ${system.planets.length} planet${system.planets.length !== 1 ? 's' : ''} — Star: ${system.star.class}`;
      const singing = this.gameState.getSingingForSystem?.(system.gx, system.gy);
      if (singing) {
        info += `\n♪ “${singing.songName}” — ${singing.playerName}`;
      }
      this.mapInfo.textContent = info;
      document.getElementById('btn-warp').disabled = false;
    } else {
      this.mapInfo.textContent = 'Select a star system to warp to';
      document.getElementById('btn-warp').disabled = true;
    }
  }

  // ── Inventory ──
  toggleInventory() {
    if (this.inventoryPanel.classList.contains('hidden')) {
      this.showInventory();
    } else {
      this.hideInventory();
    }
  }

  showInventory() {
    this.creatureList.innerHTML = '';
    const creatures = this.gameState.collectedCreatures;

    if (creatures.length === 0) {
      this.creatureList.innerHTML = '<p style="opacity:0.4;text-align:center;grid-column:1/-1">No creatures collected yet. Approach a creature and press E to collect it.</p>';
    } else {
      creatures.forEach(c => {
        const card = document.createElement('div');
        card.className = 'creature-card';
        const iconMap = { blob: '🫧', quadruped: '🦎', biped: '🧬', serpent: '🐍', floater: '🎈', insectoid: '🦗' };
        card.innerHTML = `
          <div class="creature-icon">${iconMap[c.bodyType] || '👾'}</div>
          <div class="creature-name">${c.name}</div>
        `;
        this.creatureList.appendChild(card);
      });
    }

    this.inventoryPanel.classList.remove('hidden');
  }

  hideInventory() {
    this.inventoryPanel.classList.add('hidden');
  }

  // ── Claim dialog ──
  showClaimDialog(defaultName = '') {
    this.claimNameInput.value = defaultName;
    this.claimDialog.classList.remove('hidden');
    this.claimNameInput.focus();
    return new Promise((resolve) => {
      this._claimResolve = resolve;
    });
  }

  hideClaimDialog() {
    this.claimDialog.classList.add('hidden');
    if (this._claimResolve) {
      this._claimResolve(null);
      this._claimResolve = null;
    }
  }

  _confirmClaim() {
    const name = this.claimNameInput.value.trim();
    if (hasProfanity(name)) {
      this.claimNameInput.value = '';
      this.claimNameInput.placeholder = 'Pick a friendlier name…';
      this.claimNameInput.focus();
      return;
    }
    if (name && this._claimResolve) {
      this._claimResolve(name);
      this._claimResolve = null;
      this.claimDialog.classList.add('hidden');
    }
  }

  // ── Share Dialog ──
  isShareDialogOpen() {
    return !this.shareDialog.classList.contains('hidden');
  }

  showShareDialog(url) {
    // Resolve any dangling promise from a previous dialog so no await leaks
    if (this._shareResolve) {
      this._shareResolve();
      this._shareResolve = null;
    }

    this.shareUrlInput.value = url;
    this.shareDialog.classList.remove('hidden');

    // Attempt to focus and select the text
    setTimeout(() => {
      this.shareUrlInput.select();
    }, 100);

    return new Promise((resolve) => {
      this._shareResolve = resolve;
    });
  }

  hideShareDialog() {
    this.shareDialog.classList.add('hidden');
    if (this._shareResolve) {
      this._shareResolve();
      this._shareResolve = null;
    }
  }

  // ── Audio Logs ──
  // ── Character dialogue ──
  /**
   * Show a character conversation, one line at a time.
   * Returns a promise that resolves when the last line is dismissed.
   */
  showDialogue(speakerName, lines) {
    const panel = document.getElementById('dialogue-panel');
    const speaker = document.getElementById('dialogue-speaker');
    const lineEl = document.getElementById('dialogue-line');
    const progress = document.getElementById('dialogue-progress');
    const nextBtn = document.getElementById('btn-dialogue-next');

    speaker.textContent = speakerName;
    let idx = 0;
    const render = () => {
      lineEl.textContent = lines[idx];
      progress.textContent = `${idx + 1} / ${lines.length}`;
      nextBtn.textContent = idx === lines.length - 1 ? 'Farewell' : 'Continue';
    };
    render();
    panel.classList.remove('hidden');

    return new Promise((resolve) => {
      const advance = () => {
        idx++;
        if (idx < lines.length) {
          render();
        } else {
          nextBtn.removeEventListener('click', advance);
          panel.classList.add('hidden');
          resolve();
        }
      };
      nextBtn.addEventListener('click', advance);
    });
  }

  showAudioLog(logNumber, content, toolName, toolIcon) {
    this.logTitle.textContent = `Holographic Audio Log #${logNumber}`;
    this.logContent.textContent = `"${content}"`;
    this.logToolFound.textContent = `Extracted: ${toolIcon} ${toolName}`;
    this.audioLogDialog.classList.remove('hidden');
    
    return new Promise((resolve) => {
      this._audioLogResolve = resolve;
    });
  }

  hideAudioLog() {
    this.audioLogDialog.classList.add('hidden');
    if (this._audioLogResolve) {
      this._audioLogResolve();
      this._audioLogResolve = null;
    }
  }

  // ── Notifications ──
  showNotification(message) {
    const el = document.createElement('div');
    el.className = 'notification';
    el.textContent = message;
    this.notificationArea.appendChild(el);
    setTimeout(() => el.remove(), 3000);
  }
}
